import { BUSINESS, HOURS, MEDIA } from "@/data";
import { CallButton, DirectionsButton, InstagramButton, SectionHeading } from "./Buttons";
import * as Icons from "lucide-react";

export default function StoreVisit() {
  const today = new Date().getDay();
  const todayIdx = (today + 6) % 7;

  return (
    <section id="visit" data-testid="visit-section" className="relative bg-white py-20 md:py-28 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        <SectionHeading
          eyebrow="Come Through"
          title={<>Visit Your Phone Plug<br /><span className="text-brand-red">in Miami Gardens.</span></>}
          sub="Real storefront. Real people. Walk in with your problem — leave with a solution."
          testid="visit-heading"
        />

        {/* Storefront banner — Plug-Man stationed in front of the actual store */}
        <div className="mt-12 relative rounded-3xl md:rounded-[2rem] overflow-hidden border border-slate-200 shadow-2xl bg-brand-navy" data-testid="storefront-banner">
          <div className="grid grid-cols-1 md:grid-cols-12 items-stretch">
            {/* Image side — natural square, no face crop */}
            <div className="relative md:col-span-7 lg:col-span-6 aspect-square md:aspect-auto md:min-h-[460px]">
              <img
                src={MEDIA.plugmanStore}
                alt="Plug-Man standing in front of the Your Phone Plug storefront at sunset"
                className="absolute inset-0 w-full h-full object-cover"
                style={{ objectPosition: "center 30%" }}
              />
              {/* Soft right-edge fade into navy text panel on desktop */}
              <div className="hidden md:block absolute inset-y-0 right-0 w-32 bg-gradient-to-r from-transparent to-brand-navy" />
              {/* Bottom fade for mobile readability */}
              <div className="md:hidden absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-brand-navy to-transparent" />
            </div>

            {/* Copy side */}
            <div className="md:col-span-5 lg:col-span-6 p-7 md:p-10 lg:p-14 flex flex-col justify-center text-white">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-red/20 border border-brand-red/40 mb-4 backdrop-blur-md w-fit">
                <Icons.MapPin className="w-3.5 h-3.5 text-brand-red" />
                <span className="text-xs font-bold uppercase tracking-widest text-white">20719 NW 2nd Ave</span>
              </div>
              <h3 className="font-display text-3xl md:text-4xl lg:text-5xl font-black tracking-tight leading-[1.05]">
                Plug-Man's<br />guarding the door.
              </h3>
              <p className="text-white/80 mt-3 text-sm md:text-base max-w-md">
                Drop in any time during business hours. We'll greet you, diagnose your problem for free, and explain your options.
              </p>
              <div className="mt-6 flex flex-wrap gap-2.5">
                <a
                  href={BUSINESS.directionsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="storefront-directions-btn"
                  className="inline-flex items-center gap-2 font-bold rounded-full py-3 px-5 bg-brand-red text-white animate-pulse-red transition-all active:scale-95"
                >
                  <Icons.Navigation className="w-4 h-4" />
                  Get Directions
                </a>
                <a
                  href={`tel:${BUSINESS.phoneTel}`}
                  data-testid="storefront-call-btn"
                  className="inline-flex items-center gap-2 font-bold rounded-full py-3 px-5 bg-white/15 border border-white/25 backdrop-blur-md text-white hover:bg-white/25 transition-all active:scale-95"
                >
                  <Icons.Phone className="w-4 h-4" />
                  Call First
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-5 md:gap-6">
          {/* Address & contact */}
          <div className="card-soft p-7 md:p-8 lg:col-span-1 shadow-md">
            <div className="w-12 h-12 rounded-2xl bg-green-50 border border-green-100 grid place-items-center mb-5">
              <Icons.MapPin className="w-6 h-6 text-brand-red" />
            </div>
            <h3 className="font-display text-2xl font-extrabold text-brand-navy">{BUSINESS.name}</h3>
            <p className="text-sm text-slate-600 mt-1">Local cellphone repair &amp; retail since {BUSINESS.founded}</p>

            <div className="mt-6 space-y-4 text-sm">
              <a href={BUSINESS.directionsUrl} target="_blank" rel="noopener noreferrer" data-testid="visit-address-link" className="flex items-start gap-3 text-slate-700 hover:text-brand-red transition-colors">
                <Icons.MapPin className="w-4 h-4 mt-0.5 text-brand-red shrink-0" />
                <span className="font-medium">{BUSINESS.address}</span>
              </a>
              <a href={`tel:${BUSINESS.phoneTel}`} data-testid="visit-phone-link" className="flex items-center gap-3 text-slate-700 hover:text-brand-red transition-colors">
                <Icons.Phone className="w-4 h-4 text-brand-red" />
                <span className="font-mono font-semibold">{BUSINESS.phone}</span>
              </a>
              <a href={`mailto:${BUSINESS.email}`} data-testid="visit-email-link" className="flex items-center gap-3 text-slate-700 hover:text-brand-red transition-colors">
                <Icons.Mail className="w-4 h-4 text-brand-red" />
                <span className="break-all">{BUSINESS.email}</span>
              </a>
              <a href={BUSINESS.instagram} target="_blank" rel="noopener noreferrer" data-testid="visit-ig-link" className="flex items-center gap-3 text-slate-700 hover:text-brand-red transition-colors">
                <Icons.Instagram className="w-4 h-4 text-brand-red" />
                <span>{BUSINESS.instagramHandle}</span>
              </a>
            </div>

            <div className="mt-7 flex flex-col sm:flex-row gap-2.5">
              <CallButton testid="visit-call-btn" size="md" className="w-full">Call Now</CallButton>
              <DirectionsButton testid="visit-directions-btn" variant="secondary" size="md" className="w-full" />
            </div>
            <InstagramButton testid="visit-ig-btn" size="md" className="w-full mt-2.5" />
          </div>

          {/* Map */}
          <a
            href={BUSINESS.directionsUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="visit-map-card"
            className="lg:col-span-1 relative rounded-3xl overflow-hidden border border-slate-200 shadow-md min-h-[280px] hover:shadow-xl transition-all group"
          >
            <iframe
              title="Your Phone Plug location map"
              src="https://www.google.com/maps?q=20719+NW+2nd+Ave,+Miami,+FL+33169&output=embed"
              className="absolute inset-0 w-full h-full"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/70 via-transparent to-transparent pointer-events-none" />
            <div className="absolute bottom-5 left-5 right-5 pointer-events-none">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-red text-white font-bold text-xs glow-red">
                <Icons.Navigation className="w-3.5 h-3.5" />
                Open in Google Maps
              </div>
            </div>
          </a>

          {/* Hours */}
          <div className="card-soft p-7 md:p-8 lg:col-span-1 shadow-md">
            <div className="flex items-center justify-between mb-5">
              <div className="w-12 h-12 rounded-2xl bg-orange-50 border border-orange-100 grid place-items-center">
                <Icons.Clock className="w-6 h-6 text-brand-blue" />
              </div>
              <span className="text-xs font-bold uppercase tracking-widest text-slate-400">Hours</span>
            </div>
            <h3 className="font-display text-2xl font-extrabold text-brand-navy">When we're open</h3>

            <div className="mt-5 divide-y divide-slate-100" data-testid="hours-list">
              {HOURS.map((h, i) => (
                <div
                  key={h.day}
                  className={`flex items-center justify-between py-3 ${i === todayIdx ? "bg-green-50 -mx-3 px-3 rounded-lg" : ""}`}
                  data-testid={`hours-row-${i}`}
                >
                  <span className={`text-sm font-bold ${i === todayIdx ? "text-brand-red" : "text-slate-700"}`}>
                    {h.day}
                    {i === todayIdx && <span className="ml-2 text-[10px] uppercase font-bold text-brand-red">Today</span>}
                  </span>
                  <span className={`text-sm font-mono font-semibold ${h.hours === "Closed" ? "text-slate-400" : "text-brand-navy"}`}>
                    {h.hours}
                  </span>
                </div>
              ))}
            </div>

            <p className="mt-5 text-xs text-slate-500 italic">Please call to confirm current hours.</p>
          </div>
        </div>
      </div>
    </section>
  );
}
